import { type UTMConfig,type UTMParameters, type UTMSession } from './types';

/**
 * Centralized UTM parameter tracking service
 * 
 * Captures and manages UTM parameters for attribution across the application.
 * Follows 2025 best practices by storing UTMs at session level rather than
 * duplicating on every event.
 */
class UTMTrackerService {
  private static instance: UTMTrackerService;
  private config: Required<UTMConfig>;
  private currentSession: UTMSession | null = null;

  private readonly STORAGE_KEY = 'utm_session';
  private readonly UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
  ];

  private constructor(config: UTMConfig = {}) {
    this.config = {
      attributionWindowDays: config.attributionWindowDays ?? 30,
      storagePrefix: config.storagePrefix ?? 'utm_',
      persistAcrossSessions: config.persistAcrossSessions ?? true,
    };

    // Load existing session if available
    this.loadSession();

    // Auto-capture on initialization if UTMs present in URL
    this.capture();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(config?: UTMConfig): UTMTrackerService {
    if (!UTMTrackerService.instance) {
      UTMTrackerService.instance = new UTMTrackerService(config);
    }
    return UTMTrackerService.instance;
  }

  /**
   * Capture UTM parameters from current URL
   * @returns boolean indicating if any UTMs were captured
   */
  public capture(): boolean {
    const params = this.extractUTMsFromURL();
    
    if (Object.keys(params).length === 0) {
      // No new UTMs in URL, keep existing session if valid
      return false;
    }

    // Create new session with captured UTMs
    this.currentSession = {
      parameters: params,
      capturedAt: Date.now(),
      referrer: document.referrer,
      landingPage: window.location.href,
    };

    // Persist to storage
    this.saveSession();
    
    // Clean URL if needed (remove UTM params after capture)
    this.cleanURL();
    
    return true;
  }

  /**
   * Get current UTM parameters
   * @returns UTM parameters or empty object
   */
  public getUTMs(): UTMParameters {
    // Check if session is still valid
    if (!this.isSessionValid()) {
      this.clearSession();
      return {};
    }

    return this.currentSession?.parameters || {};
  }

  /**
   * Get full session data including metadata
   */
  public getSession(): UTMSession | null {
    if (!this.isSessionValid()) {
      this.clearSession();
      return null;
    }
    return this.currentSession;
  }

  /**
   * Check if UTM parameters are currently available
   */
  public hasUTMs(): boolean {
    const utms = this.getUTMs();
    return Object.keys(utms).length > 0;
  }

  /**
   * Clear stored UTM session
   */
  public clear(): void {
    this.clearSession();
  }

  /**
   * Extract UTM parameters from URL
   */
  private extractUTMsFromURL(): UTMParameters {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: UTMParameters = {};

    this.UTM_PARAMS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param as keyof UTMParameters] = value;
      }
    });

    return utmParams;
  }

  /**
   * Remove UTM parameters from URL without reload
   */
  private cleanURL(): void {
    const url = new URL(window.location.href);
    
    // Remove UTM params from URL
    this.UTM_PARAMS.forEach(param => {
      url.searchParams.delete(param);
    });

    // Update URL without reload if params were removed
    const cleanURL = url.toString();
    if (cleanURL !== window.location.href) {
      window.history.replaceState({}, document.title, cleanURL);
    }
  }

  /**
   * Check if current session is within attribution window
   */
  private isSessionValid(): boolean {
    if (!this.currentSession) return false;

    const windowMs = this.config.attributionWindowDays * 24 * 60 * 60 * 1000;
    const sessionAge = Date.now() - this.currentSession.capturedAt;
    
    return sessionAge < windowMs;
  }

  /**
   * Load session from storage
   */
  private loadSession(): void {
    if (!this.config.persistAcrossSessions) return;

    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.currentSession = JSON.parse(stored) as UTMSession;
      }
    } catch (error) {
      console.error('[UTMTracker] Failed to load session:', error);
      this.clearSession();
    }
  }

  /**
   * Save session to storage
   */
  private saveSession(): void {
    if (!this.config.persistAcrossSessions || !this.currentSession) return;

    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.currentSession));
    } catch (error) {
      console.error('[UTMTracker] Failed to save session:', error);
    }
  }

  /**
   * Clear session from memory and storage
   */
  private clearSession(): void {
    this.currentSession = null;
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('[UTMTracker] Failed to clear session:', error);
    }
  }

  /**
   * Format UTMs for backend API (matches UserInfoForm format)
   */
  public getUTMsForAPI(): Record<string, string> {
    const utms = this.getUTMs();
    const formatted: Record<string, string> = {};
    
    (Object.entries(utms) as Array<[keyof UTMParameters, string | undefined]>).forEach(([key, value]) => {
      if (value) {
        formatted[key] = value;
      }
    });
    
    return formatted;
  }
}

// Export singleton instance
export const UTMTracker = UTMTrackerService.getInstance();